import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRompslompAuto, setRompslompAuto } from '../../../services/RompslompService';
import { PasswordModal } from './RompslompSending';
import { apiMessage } from './rompslompUtils';

// Automatic sending of invoices (default OFF). Separate from the master switch: its own status line, its own gates, its own password.
export default function RompslompAuto({ reloadSending }) {
    const { t } = useTranslation();
    const [auto, setAuto] = useState(null);
    const [ask, setAsk] = useState(false);

    const load = useCallback(async () => {
        try { setAuto((await getRompslompAuto()).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    if (!auto) return <Spinner animation="border" />;
    const allOk = auto.gates.every((g) => g.ok);
    const stopped = !auto.enabled && !!auto.stopped_reason;
    const line = auto.enabled ? t('rompslomp.auto.statusOn') : (stopped ? t('rompslomp.auto.statusStopped', { reason: auto.stopped_reason }) : t('rompslomp.auto.statusOff'));

    const change = async (password) => {
        try {
            const res = await setRompslompAuto(!auto.enabled, password);
            setAuto(res.data);
            toast.success(t(res.data.enabled ? 'rompslomp.auto.switchedOn' : 'rompslomp.auto.switchedOff'));
            setAsk(false);
            reloadSending();
        } catch (err) {
            if (err.response?.data?.gates) setAuto((a) => ({ ...a, gates: err.response.data.gates }));
            toast.error(apiMessage(err, t, 'rompslomp.errors.generic'));
        }
    };
    const detailText = (g) => (Array.isArray(g.detail) && g.detail.length ? `: ${g.detail.join(', ')}` : (typeof g.detail === 'string' && g.detail ? `: ${g.detail}` : ''));

    return (
        <div data-testid="auto-controls">
            <p className="text-muted">{t('rompslomp.auto.hint', { max: auto.batch_max })}</p>
            <p><strong data-testid="auto-status">{line}</strong></p>
            <h6>{t('rompslomp.auto.gatesTitle')}</h6>
            <ul className="list-unstyled" data-testid="auto-gates">
                {auto.gates.map((g) => (
                    <li key={g.key} data-testid={`gate-${g.key}`} data-ok={g.ok ? 'true' : 'false'}>
                        <span aria-hidden="true">{g.ok ? '✓' : '✗'}</span> {t(`rompslomp.auto.gates.${g.key}`)}{!g.ok ? detailText(g) : ''}
                        <span className="visually-hidden"> {g.ok ? t('rompslomp.auto.gateOk') : t('rompslomp.auto.gateFail')}</span>
                    </li>
                ))}
            </ul>
            {!auto.enabled && !allOk && <Alert variant="secondary">{t('rompslomp.auto.cannotSwitchOn')}</Alert>}
            <Button variant={auto.enabled ? 'outline-secondary' : 'danger'} disabled={!auto.enabled && !allOk} onClick={() => setAsk(true)} data-testid="toggle-auto">
                {auto.enabled ? t('rompslomp.auto.switchOff') : (stopped ? t('rompslomp.auto.resume') : t('rompslomp.auto.switchOn'))}
            </Button>
            <PasswordModal show={ask} onHide={() => setAsk(false)}
                title={auto.enabled ? t('rompslomp.auto.switchOff') : t('rompslomp.auto.switchOn')}
                text={auto.enabled ? t('rompslomp.auto.offText') : t('rompslomp.auto.onText')}
                confirmLabel={auto.enabled ? t('rompslomp.auto.switchOff') : (stopped ? t('rompslomp.auto.resume') : t('rompslomp.auto.switchOn'))}
                onConfirm={change} />
        </div>
    );
}
